/**
 * VendorMyLists — the vendor / business owner counterpart to MyFoodLists.
 *
 * Route: /food-pulse/vendor/lists
 *
 * Tabs (data from existing per-owner endpoints):
 *   - My Store Products  → /api/pending-products/mine  (+ each business's catalog)
 *   - Deals I'm Running  → /api/deals/mine
 *   - Surplus Listings   → /api/deals/mine?deal_type=surplus  (filtered client-side)
 *   - Donations Offered  → /api/lists/donation  (per-user offerings)
 *   - Recipes I've Shared → /api/recipes/mine
 *   - My Businesses      → /api/businesses/mine
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Store, Tag, Package, HandHeart, ChefHat, Building2,
  Loader2, ExternalLink, Plus,
} from "lucide-react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import SeoHead from "@/components/SeoHead";

const GREEN = "#0F3D2E";
const CREAM = "#FBF7EF";

const TABS = [
  { k: "products",  l: "My Store Products",  Icon: Store },
  { k: "deals",     l: "Deals I'm Running",  Icon: Tag },
  { k: "surplus",   l: "Surplus Listings",   Icon: Package },
  { k: "donations", l: "Donations Offered",  Icon: HandHeart },
  { k: "recipes",   l: "Recipes I've Shared", Icon: ChefHat },
  { k: "stores",    l: "My Businesses",      Icon: Building2 },
];

function EmptyRow({ Icon, title, body, cta, ctaTo }) {
  return (
    <div className="rounded-2xl border border-dashed p-10 text-center" style={{ borderColor: "#B8D3A1", background: "#fff" }}>
      <Icon className="w-10 h-10 mx-auto mb-3 opacity-40" />
      <div className="font-display text-xl mb-1" style={{ color: GREEN }}>{title}</div>
      <div className="text-sm opacity-70 mb-4">{body}</div>
      {cta && ctaTo && (
        <Link to={ctaTo} className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-white" style={{ background: GREEN }} data-testid="vendor-lists-empty-cta">
          <Plus className="w-4 h-4" /> {cta}
        </Link>
      )}
    </div>
  );
}

function Row({ image, title, subtitle, extra, to, testid }) {
  return (
    <Link to={to || "#"} className="flex items-center gap-3 rounded-2xl border p-3 bg-white hover:shadow-sm transition"
          style={{ borderColor: "#E9D8B4" }} data-testid={testid}>
      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0" style={{ background: CREAM }}>
        {image && <img src={image} alt="" className="w-full h-full object-cover" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-sm truncate" style={{ color: GREEN }}>{title}</div>
        {subtitle && <div className="text-xs opacity-70 truncate">{subtitle}</div>}
        {extra && <div className="text-[11px] opacity-60 mt-0.5 truncate">{extra}</div>}
      </div>
      <ExternalLink className="w-4 h-4 opacity-40 flex-shrink-0" />
    </Link>
  );
}

export default function VendorMyLists() {
  const { user } = useAuth();
  const [tab, setTab] = useState("products");
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState({
    products: [], deals: [], donations: [], recipes: [], stores: [],
  });

  const reload = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    try {
      const [pp, dl, dn, rc, bs] = await Promise.all([
        http.get(`/pending-products/mine`).catch(() => ({ data: { items: [] } })),
        http.get(`/deals/mine`).catch(() => ({ data: { items: [] } })),
        http.get(`/lists/donation`).catch(() => ({ data: { items: [] } })),
        http.get(`/recipes/mine`).catch(() => ({ data: { items: [] } })),
        http.get(`/businesses/mine`).catch(() => ({ data: { items: [] } })),
      ]);
      setState({
        products:  pp.data.items || pp.data || [],
        deals:     dl.data.items || dl.data || [],
        donations: dn.data.items || dn.data || [],
        recipes:   rc.data.items || rc.data || [],
        stores:    bs.data.items || bs.data || [],
      });
    } catch { /* ignore */ }
    setLoading(false);
  }, [user]);

  useEffect(() => { reload(); }, [reload]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: CREAM, color: GREEN }}>
        <div className="max-w-md text-center">
          <Store className="w-14 h-14 mx-auto mb-4" style={{ color: "#7CB342" }} />
          <h1 className="font-display text-3xl mb-3">Vendor lists</h1>
          <p className="opacity-80 mb-6">Sign in to manage the products, deals, surplus and recipes for your business.</p>
          <Link to="/login" className="rounded-full px-6 py-3 font-semibold text-white" style={{ background: GREEN }} data-testid="vendor-lists-login-cta">Sign in</Link>
        </div>
      </div>
    );
  }

  const surplus = (state.deals || []).filter((d) => (d.deal_type || d.type || "").toLowerCase().includes("surplus"));
  const runningDeals = (state.deals || []).filter((d) => !((d.deal_type || d.type || "").toLowerCase().includes("surplus")));

  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="vendor-my-lists">
      <SeoHead section="food-pulse-vendor-lists" title="Vendor lists — Food Pulse" description="Manage your store products, deals, surplus, donations and recipes." />
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="font-display text-3xl sm:text-4xl" style={{ color: GREEN }}>Your vendor lists</h1>
          <p className="text-sm opacity-70 mt-1">Everything you offer on FoodPulse — products, deals, surplus, donations and recipes.</p>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          {TABS.map(({ k, l, Icon }) => (
            <button key={k} onClick={() => setTab(k)}
                    className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ${tab === k ? "text-white" : "opacity-70 hover:opacity-100"}`}
                    style={{ background: tab === k ? GREEN : "transparent" }}
                    data-testid={`vendor-tab-${k}`}>
              <Icon className="w-4 h-4" /> {l}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16 opacity-60"><Loader2 className="w-6 h-6 animate-spin" /></div>
        ) : (
          <>
            {tab === "products" && (state.products.length ? (
              <div className="grid gap-3" data-testid="vendor-products-list">
                {state.products.map((p) => (
                  <Row key={p.id} image={(p.image_urls || [])[0]} title={p.name || p.slug} subtitle={p.brand || p.product_type}
                       extra={p.status ? `Status: ${p.status}` : null}
                       to={`/food-pulse/food/${p.slug || ""}`}
                       testid={`vendor-product-${p.id}`} />
                ))}
              </div>
            ) : (
              <EmptyRow Icon={Store} title="No store products yet" body="Submit new products from any product page or the scanner."
                cta="Add Product" ctaTo="/food-pulse/lists/wishlist/new" />
            ))}

            {tab === "deals" && (runningDeals.length ? (
              <div className="grid gap-3" data-testid="vendor-deals-list">
                {runningDeals.map((d) => (
                  <Row key={d.id} image={(d.image_urls || [])[0]} title={d.title || d.slug}
                       subtitle={d.vendor_name || d.business_name}
                       extra={d.deal_price != null ? `$${d.deal_price}${d.unit ? "/" + d.unit : ""}` : null}
                       to={`/food-pulse/deals/${d.slug || d.id}`}
                       testid={`vendor-deal-${d.id}`} />
                ))}
              </div>
            ) : (
              <EmptyRow Icon={Tag} title="No deals running" body="Post a deal to reach shoppers near you."
                cta="Post a Deal" ctaTo="/food-pulse/spot?mode=deal" />
            ))}

            {tab === "surplus" && (surplus.length ? (
              <div className="grid gap-3" data-testid="vendor-surplus-list">
                {surplus.map((d) => (
                  <Row key={d.id} image={(d.image_urls || [])[0]} title={d.title || d.slug}
                       subtitle={d.vendor_name || d.business_name}
                       to={`/food-pulse/deals/${d.slug || d.id}`}
                       testid={`vendor-surplus-${d.id}`} />
                ))}
              </div>
            ) : (
              <EmptyRow Icon={Package} title="No surplus listings" body="List surplus food nearing best-by dates at deep discounts."
                cta="Post Surplus" ctaTo="/food-pulse/spot?mode=surplus" />
            ))}

            {tab === "donations" && (state.donations.length ? (
              <div className="grid gap-3" data-testid="vendor-donations-list">
                {state.donations.map((d) => (
                  <Row key={d.id} image={d.food_item_image} title={d.food_item_name}
                       subtitle={d.charity_slug ? `To: ${d.charity_slug}` : "Available for donation"}
                       extra={d.quantity ? `${d.quantity} ${d.unit || ""}` : null}
                       to={`/food-pulse/food/${d.food_item_slug}`}
                       testid={`vendor-donation-${d.id}`} />
                ))}
              </div>
            ) : (
              <EmptyRow Icon={HandHeart} title="No donations offered" body="From any product page, tap 'Share as Donation' to offer surplus food to nearby charities." />
            ))}

            {tab === "recipes" && (state.recipes.length ? (
              <div className="grid gap-3" data-testid="vendor-recipes-list">
                {state.recipes.map((r) => (
                  <Row key={r.id} image={r.image_url || (r.image_urls || [])[0]} title={r.title || r.name || r.slug}
                       subtitle={r.cuisine || r.difficulty}
                       to={`/food-pulse/recipes/${r.slug || r.id}`}
                       testid={`vendor-recipe-${r.id}`} />
                ))}
              </div>
            ) : (
              <EmptyRow Icon={ChefHat} title="No recipes shared yet" body="Publish signature recipes to build your following."
                cta="Share a Recipe" ctaTo="/food-pulse/recipes/new" />
            ))}

            {tab === "stores" && (state.stores.length ? (
              <div className="grid gap-3" data-testid="vendor-stores-list">
                {state.stores.map((b) => (
                  <Row key={b.id || b.slug} image={b.hero_image || b.logo_url} title={b.name}
                       subtitle={[b.city, b.state, b.country_code].filter(Boolean).join(" · ")}
                       extra={b.verified ? "✓ Verified" : (b.claim_status || null)}
                       to={`/food-pulse/businesses/${b.slug}`}
                       testid={`vendor-store-${b.id || b.slug}`} />
                ))}
              </div>
            ) : (
              <EmptyRow Icon={Building2} title="No businesses claimed" body="Claim your storefront on FoodPulse to unlock deals, surplus and recipe publishing."
                cta="Claim a Business" ctaTo="/food-pulse/city" />
            ))}
          </>
        )}
      </div>
    </div>
  );
}
